import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { colorOf } from "@/lib/constants";
import { X } from "lucide-react";

/**
 * PetPeek — pet equivalent of MoodPeek. Long-press a pet tile and
 * see the last 7 days of care at a glance: how many walks, feeds and
 * water changes happened each day. Top-level row is anchored above
 * the tile (or screen-centred on small screens).
 *
 * Data: `/api/pets/{id}/history?days=7` already returns one entry
 * per day with all care actions, so we just count by `kind`.
 *
 * Mirrors MoodPeek's rendering rules — same anchor logic, ESC + back-
 * drop to close, same 7-cell strip — so the two peeks read as a
 * single design pattern across humans + pets.
 */
export default function PetPeek({ pet, anchorRect, onClose }) {
  const [history, setHistory] = useState(null); // null = loading, [] = empty
  const [err, setErr] = useState(null);
  const popRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setHistory(null);
    setErr(null);
    api
      .petHistory(pet.id, 7)
      .then((res) => {
        if (!alive) return;
        setHistory(res?.days || []);
      })
      .catch((e) => {
        if (!alive) return;
        // 403 = pet outside care-log allowance (free tier with multiple
        // pets) — surface as a soft note rather than an error scream.
        if (e?.response?.status === 403) {
          setErr("History is part of paid care log");
        } else {
          setErr("Couldn't load recent care");
        }
        setHistory([]);
      });
    return () => {
      alive = false;
    };
  }, [pet.id]);

  // Close on ESC.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Build the 7-day row: 6 days back at left, today at right. Each
  // slot gets the count of walks / feeds / waters from history.
  const days = (() => {
    const out = [];
    const now = new Date();
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today0);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({ key, date: d, walks: 0, fed: 0, water: 0 });
    }
    if (Array.isArray(history)) {
      for (const day of history) {
        const slot = out.find((s) => s.key === day.date);
        if (!slot) continue;
        for (const e of day.entries || []) {
          if (e.kind === "walk") slot.walks += 1;
          else if (e.kind === "fed") slot.fed += 1;
          else if (e.kind === "water") slot.water += 1;
        }
      }
    }
    return out;
  })();

  // Week totals — what the family did over the last 7 days. Useful
  // single-glance context under the strip.
  const weekStats = (() => {
    let walks = 0, fed = 0, water = 0;
    for (const d of days) {
      walks += d.walks;
      fed += d.fed;
      water += d.water;
    }
    return { walks, fed, water };
  })();

  // Position: prefer anchored above the card, else centre.
  const positionStyle = (() => {
    if (!anchorRect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const panelW = 320;
    const panelH = 220;
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchorRect.left + anchorRect.width / 2 - panelW / 2;
    left = Math.max(12, Math.min(left, vw - panelW - 12));
    let top = anchorRect.top - panelH - gap;
    if (top < 12) top = Math.min(anchorRect.bottom + gap, vh - panelH - 12);
    return { top, left };
  })();

  const dayLabel = (date, i) => {
    if (i === 6) return "Today";
    if (i === 5) return "Yest";
    return date.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 2);
  };

  const color = colorOf(pet.color);

  return (
    <div
      className="fixed inset-0 z-[90]"
      onClick={onClose}
      data-testid={`pet-peek-backdrop-${pet.id}`}
      aria-hidden="false"
    >
      <div
        ref={popRef}
        onClick={(e) => e.stopPropagation()}
        className="absolute bg-[color:var(--surface)] border border-[color:var(--line)] rounded-xl shadow-2xl p-4 w-[320px] animate-in fade-in zoom-in-95 duration-150"
        style={{ ...positionStyle }}
        data-testid={`pet-peek-${pet.id}`}
        role="dialog"
        aria-label={`${pet.name}'s week in care`}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xl flex-shrink-0 overflow-hidden"
              style={{ background: color }}
              aria-hidden="true"
            >
              {pet.avatar_image ? (
                <img
                  src={pet.avatar_image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                pet.avatar_emoji
              )}
            </div>
            <div className="min-w-0">
              <div className="font-display text-base leading-tight truncate">
                {pet.name}
              </div>
              <div className="label-tiny text-[color:var(--ink-3)]">
                7-day care
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center -mt-1 -mr-1"
            data-testid={`pet-peek-close-${pet.id}`}
            aria-label="Close pet peek"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* 7-day strip — each cell shows the day's care icons stacked
            in their tinted slots so the family can see at a glance
            "Wed had 1 walk + fed + water, Thu only fed". Empty days
            render as a faint dash. */}
        <div
          className="grid grid-cols-7 gap-1"
          data-testid={`pet-peek-strip-${pet.id}`}
        >
          {days.map((slot, i) => {
            const total = slot.walks + slot.fed + slot.water;
            const title = total
              ? `${slot.date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })} — ${slot.walks} walk${slot.walks === 1 ? "" : "s"}, ${slot.fed} fed, ${slot.water} water`
              : `${slot.date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })} — no care logged`;
            return (
              <div
                key={slot.key}
                className="flex flex-col items-center gap-1"
                title={title}
                data-testid={`pet-peek-day-${slot.key}`}
              >
                <div
                  className="w-full aspect-square rounded-lg border border-[color:var(--line-soft)] flex flex-col items-center justify-center text-[10px] gap-px"
                  style={{
                    background: total ? "#CDE6D2" : "var(--bg-soft)",
                    opacity: total ? 1 : 0.55,
                  }}
                  aria-label={title}
                >
                  {total ? (
                    <>
                      {slot.walks > 0 && (
                        <span className="leading-none">
                          🚶{slot.walks > 1 ? slot.walks : ""}
                        </span>
                      )}
                      {slot.fed > 0 && (
                        <span className="leading-none">🍽️</span>
                      )}
                      {slot.water > 0 && (
                        <span className="leading-none">💧</span>
                      )}
                    </>
                  ) : (
                    <span className="leading-none text-[color:var(--ink-3)]">·</span>
                  )}
                </div>
                <div
                  className={`text-[10px] leading-none ${
                    i === 6
                      ? "font-semibold text-[color:var(--ink)]"
                      : "text-[color:var(--ink-3)]"
                  }`}
                >
                  {dayLabel(slot.date, i)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — 7-day totals or empty / error states */}
        <div className="mt-3 text-[12px] leading-snug text-[color:var(--ink-2)]">
          {history === null && <span>Loading…</span>}
          {err && <span className="text-[color:var(--rough-strong)]">{err}</span>}
          {history && !err && (weekStats.walks + weekStats.fed + weekStats.water > 0) && (
            <span>
              Last 7 days · <strong>{weekStats.walks}</strong> walk
              {weekStats.walks === 1 ? "" : "s"} · <strong>{weekStats.fed}</strong> fed ·{" "}
              <strong>{weekStats.water}</strong> water
            </span>
          )}
          {history && !err && (weekStats.walks + weekStats.fed + weekStats.water === 0) && (
            <span>No care logged in the last 7 days yet.</span>
          )}
        </div>
      </div>
    </div>
  );
}
